import React from "react";
import { PageLayout } from "./PageLayout";
import { PageContent } from "./PageContent";
import { PageHeader } from "./PageHeader";
import { Skeleton } from "./Skeleton";
import { ChartSkeleton } from "./ChartSkeleton";

type Variant = "cards" | "chart" | "table" | "form";

const Card: React.FC<{ className?: string; children?: React.ReactNode }> = ({
  className = "",
  children,
}) => (
  <div className={`bg-card rounded-lg shadow-sm border border-border p-6 ${className}`}>
    {children}
  </div>
);

function VariantBody({ variant }: { variant: Variant }) {
  switch (variant) {
    case "chart":
      return (
        <div className="space-y-6">
          <Card className="flex flex-wrap gap-3">
            {[150, 90, 90, 110, 90].map((w, i) => (
              <Skeleton key={i} height={32} width={w} className="rounded-full" />
            ))}
          </Card>
          <ChartSkeleton variant="line" showTitle />
          <ChartSkeleton variant="bar" showTitle />
        </div>
      );
    case "table":
      return (
        <Card>
          <Skeleton variant="text" height={18} width={180} className="mb-4" />
          <div className="space-y-3">
            {Array.from({ length: 8 }).map((_, i) => (
              <Skeleton key={i} height={40} />
            ))}
          </div>
        </Card>
      );
    case "form":
      return (
        <div className="space-y-6">
          {[0, 1].map((c) => (
            <Card key={c}>
              <Skeleton variant="text" height={18} width={160} className="mb-4" />
              <div className="space-y-3">
                <Skeleton height={40} />
                <Skeleton height={40} />
              </div>
            </Card>
          ))}
        </div>
      );
    case "cards":
    default:
      return (
        <div className="space-y-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[0, 1, 2].map((i) => (
              <Skeleton key={i} height={120} className="rounded-lg" />
            ))}
          </div>
          <Skeleton height={260} className="rounded-lg" />
        </div>
      );
  }
}

interface PageLoadingProps {
  title: string;
  description?: React.ReactNode;
  badge?: React.ReactNode;
  maxWidth?: React.ComponentProps<typeof PageContent>["maxWidth"];
  /** Built-in skeleton shape; ignored when `children` is provided. */
  variant?: Variant;
  /** Custom skeleton body for pages whose layout none of the variants match. */
  children?: React.ReactNode;
}

/**
 * Unified page loading state (review v2 §2 / P0). Always renders the page header
 * plus a layout-matched skeleton, so the header never flickers between routes and
 * content doesn't pop into a different layout than the loader. Replaces the
 * per-page bare "Loading…" strings that each centred differently (or lost the
 * header entirely).
 */
export function PageLoading({
  title,
  description,
  badge,
  maxWidth = "5xl",
  variant = "cards",
  children,
}: PageLoadingProps) {
  return (
    <PageLayout>
      <PageContent maxWidth={maxWidth}>
        <PageHeader title={title} description={description} badge={badge} />
        {children ?? <VariantBody variant={variant} />}
      </PageContent>
    </PageLayout>
  );
}
